import { CloseCircleOutlined } from "@ant-design/icons";
import { DEFAULT_EXPIRY_DURATION_OPTIONS } from "@p0security/shared/configuration/constant";
import { DurationOption } from "@p0security/shared/configuration/types";
import {
  sortApprovalOptions,
  timeUnitLabelOptions,
  timeUnitLabels,
} from "@p0security/shared/permission-requests/util";
import {
  Col,
  Divider,
  Form,
  InputNumber,
  List,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
  message,
} from "antd";
import { useAuthFetch } from "components/Login/hook";
import { AuthzButton } from "components/common/AuthzButton";
import { useCallback, useMemo, useState } from "react";
import { handleErrorWithToastMessage } from "utils/error";

import { useDurationSettings } from "./useDurationSettings";

const style: React.CSSProperties = { padding: "8px 0", flex: "1  1  0px" };

export const CustomExpiryOptions: React.FC = () => {
  const optionsDoc = useDurationSettings();

  const options = useMemo(
    () =>
      [
        ...(optionsDoc.doc?.data?.options ?? DEFAULT_EXPIRY_DURATION_OPTIONS),
      ].sort(sortApprovalOptions),
    [optionsDoc.doc]
  );

  const authFetch = useAuthFetch(handleErrorWithToastMessage);
  const [time, setTime] = useState<number | null>(1);
  const [unit, setUnit] = useState<keyof typeof timeUnitLabels>("m");

  const handleAddOption = useCallback(async () => {
    if (!time || !unit) return;
    const response = await authFetch("settings/expiry-options", {
      method: "POST",
      json: { time, unit },
    });
    if (!response) return;
    message.success("Option added");
  }, [authFetch, time, unit]);

  const handleRemove = useCallback(
    async ({ value }: DurationOption) => {
      const response = await authFetch(`settings/expiry-options/${value}`, {
        method: "DELETE",
      });
      if (!response) return;
      message.success("Option removed");
    },
    [authFetch]
  );

  const handleReset = useCallback(async () => {
    const response = await authFetch("settings/expiry-options/_reset", {
      json: {},
      method: "POST",
    });
    if (!response) return;
    message.success("Reset expiry options");
  }, [authFetch]);

  const renderExpiryOption = useCallback(
    (item: DurationOption) => (
      <List.Item>
        <div>{item.value}</div>
        <div style={{ float: "right" }}>
          <Popconfirm
            placement="left"
            title="Delete Option"
            // Already memoized
            // eslint-disable-next-line react/jsx-no-bind
            onConfirm={() => handleRemove(item)}
            okText="Yes"
            cancelText="No"
          >
            <CloseCircleOutlined />
          </Popconfirm>
        </div>
      </List.Item>
    ),
    [handleRemove]
  );

  return (
    <div>
      <Typography.Title level={4}>Expiry Options</Typography.Title>
      <div>
        <Typography.Text type="secondary">
          Manage access duration options displayed for approvers.
          <br />
          Note, Approvers may always enter a custom time manually.
        </Typography.Text>
        <br />
        {optionsDoc.loading ? (
          <Skeleton.Input active block={true} />
        ) : (
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col className="gutter-row" xs={24} md={12}>
              <div style={style}>
                <List
                  size="small"
                  header={<div>Options</div>}
                  bordered
                  dataSource={options}
                  renderItem={renderExpiryOption}
                />
              </div>
            </Col>
            <Col className="gutter-row" span={12}>
              <div style={style}>
                <Form
                  name="wrap"
                  labelCol={{ flex: "110px" }}
                  labelAlign="left"
                  labelWrap
                  wrapperCol={{ flex: 1 }}
                  colon={false}
                  style={{ maxWidth: 700 }}
                >
                  <Space>
                    <Space.Compact>
                      <InputNumber min={1} value={time} onChange={setTime} />
                      <Select
                        options={timeUnitLabelOptions}
                        value={unit}
                        onChange={setUnit}
                        style={{ width: 97 }}
                      />
                    </Space.Compact>
                    <Divider />

                    <AuthzButton
                      roles={["owner"]}
                      type="primary"
                      onClick={handleAddOption}
                    >
                      Add Option
                    </AuthzButton>
                  </Space>
                </Form>
                <p>
                  <Popconfirm
                    placement="bottom"
                    title="Reset Options"
                    onConfirm={handleReset}
                    okText="Yes"
                    cancelText="No"
                  >
                    <AuthzButton roles={["owner"]} type="default">
                      Reset to default options
                    </AuthzButton>
                  </Popconfirm>
                </p>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
